import {
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, Observable, throwError, timeout, TimeoutError } from 'rxjs';

/** Long enough that no healthy request reaches it, short enough to bound a hang. */
export const DEFAULT_HTTP_TIMEOUT_MS = 60_000;

/**
 * Per-request override. `null` disables the timeout entirely — use for uploads
 * and long-running exports.
 *
 *   this.http.get(url, { context: new HttpContext().set(HTTP_TIMEOUT, 10_000) })
 */
export const HTTP_TIMEOUT = new HttpContextToken<number | null>(() => DEFAULT_HTTP_TIMEOUT_MS);

/**
 * WKWebView on iOS can leave an XHR pending indefinitely when the app is
 * suspended mid-flight — the request neither completes nor errors on resume.
 * Anything awaiting it (route guards, loading spinners) then waits forever.
 * This bounds every request so a hang surfaces as a failure the app can handle.
 */
@Injectable({
  providedIn: 'root',
})
export class TimeoutInterceptor implements HttpInterceptor {
  public intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    const configured = request.context.get(HTTP_TIMEOUT);

    // Uploads are legitimately slow and progress-driven; never cut them off.
    if (configured === null || request.body instanceof FormData) {
      return next.handle(request);
    }

    return next.handle(request).pipe(
      timeout(configured),
      catchError((error: unknown) => {
        if (!(error instanceof TimeoutError)) {
          return throwError(() => error);
        }
        // Surface as a normal network failure (status 0) so the rest of the
        // interceptor chain and all existing error handling treat it uniformly
        // — in particular, so it is never mistaken for an auth failure.
        // request.url, not urlWithParams: query strings carry reservation ids
        // and guest emails, and this message ends up in error reporting.
        return throwError(
          () =>
            new HttpErrorResponse({
              status: 0,
              statusText: `Request timed out after ${configured}ms`,
              url: request.url,
              error: new Error(`Timeout: ${request.method} ${request.url}`),
            }),
        );
      }),
    );
  }
}
